import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { Loader } from "@/components/kit";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import CompanyDetail from "@/pages/CompanyDetail";
import Branches from "@/pages/Branches";
import BranchDetail from "@/pages/BranchDetail";
import UsersPage from "@/pages/Users";
import Cabins from "@/pages/Cabins";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import TicketConfig from "@/pages/TicketConfig";
import Visitors from "@/pages/Visitors";
import MeetingRooms from "@/pages/MeetingRooms";
import Bookings from "@/pages/Bookings";
import Reports from "@/pages/Reports";
import AuditLogs from "@/pages/AuditLogs";
import VisitorHistory from "@/pages/VisitorHistory";
import SettingsPage from "@/pages/Settings";
import WallDisplayAdmin from "@/pages/WallDisplayAdmin";

import VisitorRegister from "@/pages/public/VisitorRegister";
import EmployeeApproval from "@/pages/public/EmployeeApproval";
import VisitorPass from "@/pages/public/VisitorPass";
import RoomBooking from "@/pages/public/RoomBooking";
import BranchBooking from "@/pages/public/BranchBooking";
import WallDisplay from "@/pages/public/WallDisplay";
import CabinAuth from "@/pages/public/CabinAuth";
import CabinPortal from "@/pages/public/CabinPortal";
import CabinTickets from "@/pages/public/CabinTickets";
import CabinVisitors from "@/pages/public/CabinVisitors";
import CabinMeetings from "@/pages/public/CabinMeetings";
import CabinDashboard from "@/pages/public/CabinDashboard";

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* public */}
          <Route path="/login" element={<Login />} />
          <Route path="/visitor/:publicId" element={<VisitorRegister />} />
          <Route path="/visitor/approve/:token" element={<EmployeeApproval />} />
          <Route path="/visitor/pass/:token" element={<VisitorPass />} />
          <Route path="/book/branch/:publicId" element={<BranchBooking />} />
          <Route path="/book/room/:publicId" element={<RoomBooking />} />
          <Route path="/display/:publicId" element={<WallDisplay />} />
          <Route path="/cabin/:publicId" element={<CabinAuth />} />
          <Route path="/cabin-portal" element={<CabinPortal />} />
          <Route path="/cabin-tickets" element={<CabinTickets />} />
          <Route path="/cabin-visitors" element={<CabinVisitors />} />
          <Route path="/cabin-meetings" element={<CabinMeetings />} />
          <Route path="/cabin-dashboard" element={<CabinDashboard />} />

          {/* protected */}
          <Route element={<Protected />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/companies/:cid" element={<CompanyDetail />} />
            <Route path="/branches" element={<Branches />} />
            <Route path="/branches/:bid" element={<BranchDetail />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/cabins" element={<Cabins />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/tickets/:id" element={<TicketDetail />} />
            <Route path="/ticket-config" element={<TicketConfig />} />
            <Route path="/visitors" element={<Visitors />} />
            <Route path="/rooms" element={<MeetingRooms />} />
            <Route path="/bookings" element={<Bookings />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/audit" element={<AuditLogs />} />
            <Route path="/history" element={<VisitorHistory />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/wall" element={<WallDisplayAdmin />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
